/** @jsxImportSource ../../i18n */
import React, { useEffect, useState } from 'react';
import { Target } from 'lucide-react';
import api from '../../services/api';
import { MascotAvatar } from '../mascotCatalog';
import { formatDate, formatNumber } from '../../theme/format';
import { Badge, Card, CardHeader } from '../ui';

// A confident call is at 30 % or less, or 70 % or more; an expert is "sure
// of itself and wrong" when most of at least three such calls missed.
export const overconfident = (e) => e.confident >= 3 && e.confident_hits / e.confident < 0.5;

// skillText says how the expert compares with always answering 50 % (pure).
export const skillText = (skill) => {
  if (skill === null || skill === undefined) return '—';
  if (Math.abs(skill) < 0.5) return 'autant que pile ou face';
  return skill > 0
    ? `${formatNumber(skill, { decimals: 0 })} % mieux que pile ou face`
    : `${formatNumber(-skill, { decimals: 0 })} % moins bien que pile ou face`;
};

// ReliabilityDiagram: forecast (x) against what happened (y) per bin; a
// calibrated expert follows the diagonal.
export const ReliabilityDiagram = ({ bins, name }) => {
  const used = (bins || []).filter((b) => b.count > 0);
  const label = `Calibration de ${name} : ${used.map((b) => `annoncé ${formatNumber(b.forecast, { decimals: 0 })} %, observé ${formatNumber(b.observed, { decimals: 0 })} % (${b.count})`).join(' ; ') || 'aucune prévision notée'}`;
  const pt = (b) => [8 + b.forecast * 0.84, 92 - b.observed * 0.84];
  return (
    <svg viewBox="0 0 100 100" className="h-24 w-24 flex-shrink-0" role="img" aria-label={label}>
      <rect x="8" y="8" width="84" height="84" rx="4" className="text-line" fill="none" stroke="currentColor" strokeWidth="1" />
      <line x1="8" y1="92" x2="92" y2="8" className="text-fg-faint" stroke="currentColor" strokeWidth="1" strokeDasharray="3 3" />
      {used.length > 1 && <polyline points={used.map((b) => pt(b).join(',')).join(' ')} className="text-gold-500" fill="none" stroke="currentColor" strokeWidth="1.5" />}
      {used.map((b) => {
        const [x, y] = pt(b);
        return <circle key={b.from} cx={x} cy={y} r={Math.min(7, 2.5 + Math.sqrt(b.count))} className="text-gold-300" fill="currentColor" />;
      })}
    </svg>
  );
};

const ExpertRow = ({ e, best }) => (
  <li className="flex items-center gap-4 py-4 first:pt-0 last:pb-0" data-testid="calibration-expert">
    <ReliabilityDiagram bins={e.bins} name={e.name} />
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <MascotAvatar variant={e.mascot_variant} name={e.name} size="sm" />
        <span className="text-sm font-medium text-fg">{e.name}</span>
        {best && <Badge tone="gold">Le mieux calibré</Badge>}
        {overconfident(e) && <Badge tone="loss">Sûr de lui à tort</Badge>}
      </div>
      <p className="mt-1.5 text-sm text-fg-body">
        Brier <span className="num text-fg">{formatNumber(e.brier, { decimals: 3 })}</span> · {skillText(e.skill)}
      </p>
      <p className="num mt-0.5 text-xs text-fg-subtle">
        {e.scored} {e.scored > 1 ? 'prévisions notées' : 'prévision notée'}
        {e.confident > 0 && <> · sûr de lui : juste {e.confident_hits} fois sur {e.confident}</>}
        {' · '}voix ×{formatNumber(e.vote_weight, { decimals: 2 })} en vote par calibration
      </p>
    </div>
  </li>
);

// The experts' calibration: the probabilities they state that a line beats
// the CAC 40 over 20 sessions, scored once those sessions have passed.
export const CalibrationSection = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    api.get('/analysis/panel-calibration')
      .then((res) => alive && setData(res.data))
      .catch((err) => alive && setError(err?.response?.data?.error || 'Calibration indisponible.'));
    return () => { alive = false; };
  }, []);

  if (error) return <Card><CardHeader icon={Target} title="Calibration des experts" /><p role="alert" className="text-sm text-loss-300">{error}</p></Card>;
  if (!data) return null;
  const experts = data.experts || [];
  return (
    <Card data-testid="calibration">
      <CardHeader
        icon={Target}
        title="Calibration des experts"
        description={`Pour chaque ligne et valeur suivie, chaque expert annonce la probabilité qu’elle batte le CAC 40 sur ${data.horizon} séances. Un expert bien calibré suit la diagonale : quand il annonce 70 %, la valeur bat l’indice 7 fois sur 10.`}
      />
      {experts.length === 0 ? (
        <p className="text-sm text-fg-muted">
          {data.forecasts > 0
            ? `${data.pending} prévision${data.pending > 1 ? 's' : ''} en attente : elles sont notées ${data.horizon} séances après chaque débat.`
            : 'Les experts annoncent leurs probabilités à chaque débat quotidien : les premières seront notées 20 séances plus tard.'}
        </p>
      ) : (
        <>
          <ul className="divide-y divide-line">
            {experts.map((e, i) => <ExpertRow key={e.name} e={e} best={i === 0 && e.skill > 0} />)}
          </ul>
          <p className="mt-5 text-sm text-fg-muted" data-testid="calibration-panel">
            Le panel (moyenne des experts) : Brier <span className="num text-fg">{formatNumber(data.panel.brier, { decimals: 3 })}</span>, {skillText(data.panel.skill)}.
            {data.pending > 0 && <> {data.pending} prévision{data.pending > 1 ? 's' : ''} en attente.</>}
          </p>
        </>
      )}
      <p className="mt-4 text-xs leading-relaxed text-fg-subtle">
        Score de Brier : moyenne de (probabilité − résultat)², le résultat valant 1 si la valeur a battu le CAC 40, depuis la clôture précédant le rapport. 0 est parfait, 0,25 revient à répondre 50 % à tout. Seules les analyses indépendantes comptent, avant la table ronde.
        {data.since && <> Depuis le {formatDate(data.since, 'long')}.</>}
      </p>
    </Card>
  );
};

export default CalibrationSection;
